/**
 * URL Token Extraction Hook
 * 
 * Extracts invitation or authentication tokens from URL parameters
 * and delegates to the token processor for validation and handling.
 * 
 * Features:
 * - Automatic token extraction from URL on component mount
 * - Support for multiple token parameter names ('token' and 'invitation')
 * - Client-side only execution with SSR safety checks
 * - Seamless integration with token processing logic
 */
// src/hooks/useToken.js
import { useEffect, useState } from 'react';
import { useTokenProcessor } from './useTokenProcessor';

/**
 * Simplified hook that extracts token from URL and processes it
 * 
 * @returns {Object} Token state and methods from the token processor
 */
export const useToken = () => {
  // Track the extracted token
  const [token, setToken] = useState(null);

  /**
   * Extract token from URL on mount
   * Looks for either 'token' or 'invitation' query parameters
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Extract token from URL query parameters - ADD LEGACY SUPPORT
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token') ||
      params.get('invitation') ||
      params.get('invitationToken');

    // Set token if found
    if (urlToken) {
      setToken(urlToken);

      if (process.env.NODE_ENV === 'development') {
        console.log('Token extracted from URL parameters');
      }
    }
  }, []);

  // Use the processor hook with the extracted token
  return useTokenProcessor(token);
};

export default useToken;